import moment from 'moment';
import React, { useState } from 'react';
import { Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../components/doctors/partials/Header';
import NoteDropdown from '../../components/doctors/partials/noteDropdown';
import TopLeftMenu from '../../components/doctors/partials/topLeftMenu';
import LoadingButton from '../../components/forms/LoadingButton';
import { useDispatch } from 'react-redux';
import { getRequest, postRequest } from '../../utils/axiosRequests';
import { getPatientIdForDoctor } from '../../utils/helpers';
import { itemElementNotes } from '../../store/slices/prescriptionSlice';
import { ReactComponent as BackArrow } from '../../assets/icons/arrow-square-left.svg';

const INITIAL_FORM = {
  patientId: '',
  patientDocId: '',
  patient_name: '',
  age: '',
  registration_no: '',
  ward_bed: '',
  indication: '',
  operation: '',
  anaesthesia: '',
  surgeons_name: '',
  eye: '',
  status: 'pending',
  ot_date: new Date().toISOString().split('T')[0],
};

function NewOtEntry() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [patientOptions, setPatientOptions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [backendError, setBackendError] = useState('');
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.authReducer.data);
  const activeDoctor = useSelector(
    (state) => state.doctorsOfAssistantReducer.activeDoctor,
  );
  const allNotes = useSelector(
    (state) =>
      state?.prescriptionReducer?.prescriptionInfo?.itemElementNotes || [],
  );
  const navigate = useNavigate();

  const anaesthesiaSuggestions = allNotes.filter(
    (n) => n.elementType === 'otAnaesthesia',
  );
  const surgeonSuggestions = allNotes.filter(
    (n) => n.elementType === 'otSurgeons',
  );

  const saveNoteIfNew = (value, elementType, suggestions) => {
    if (!value) return;
    const exists = suggestions.some(
      (n) => n.note?.toLowerCase() === value.toLowerCase(),
    );
    if (!exists) {
      const newNote = { elementType, itemElement: elementType, note: value };
      postRequest('prescription-item-element-notes', newNote)
        .then(() => {
          dispatch(itemElementNotes([...allNotes, newNote]));
        })
        .catch(() => {});
    }
  };

  const handlePatientSearch = (query) => {
    setPatientSearchLoading(true);
    const doctorId =
      userInfo.userType === 'assistant' ? activeDoctor.id : userInfo.id;
    const url = `patients/all-patients-list?q=${encodeURIComponent(query)}&perPage=10&page=1&doctorId=${doctorId}`;
    getRequest(url)
      .then((data) => {
        setPatientOptions(data?.patients || []);
        setPatientSearchLoading(false);
      })
      .catch(() => setPatientSearchLoading(false));
  };

  const handlePatientSelect = (selected) => {
    if (!selected.length) return;
    const patient = selected[0];
    const age = patient.dob ? `${moment().diff(patient.dob, 'years')} yrs` : '';
    const docPatientId = getPatientIdForDoctor(
      patient.patientIdForDoctor,
      userInfo.userType === 'assistant' ? activeDoctor : userInfo,
    );
    setFormData((f) => ({
      ...f,
      patientId: patient._id || patient.id || '',
      patientDocId: docPatientId || '',
      patient_name: patient.name || '',
      age: age,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoadingStatus(true);
    setBackendError('');
    postRequest('ot-list', formData)
      .then((data) => {
        saveNoteIfNew(
          formData.anaesthesia,
          'otAnaesthesia',
          anaesthesiaSuggestions,
        );
        saveNoteIfNew(formData.surgeons_name, 'otSurgeons', surgeonSuggestions);
        setLoadingStatus(false);
        toast.success(data?.message || 'OT entry created successfully', {
          position: toast.POSITION.TOP_RIGHT,
        });
        navigate('/ot-list');
      })
      .catch((error) => {
        setLoadingStatus(false);
        setBackendError(error.message);
      });
  };

  return (
    <div>
      <Header />
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-md-12 sticky-header shadow header-sticky-second">
            <TopLeftMenu />
          </div>
        </div>
        <Row className="rx-body">
          <div className="col-xl-12 col-md-12 col-lg-12">
            <div className="card shadow mb-4">
              <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                <span style={{ width: '25%' }}>
                  <BackArrow
                    className="trash-icon square-back-arrow cursor-pointer"
                    onClick={() => navigate('/ot-list')}
                  />{' '}
                  <Link to="/ot-list" className="back-link-text">
                    <span>Back to OT List</span>
                  </Link>
                </span>
                <h6
                  className="m-0 font-weight-bold center black"
                  style={{ width: '75%' }}
                >
                  New OT Entry
                </h6>
              </div>

              <div className="card-body patient-history appointment-card-body">
                <div className="v-error text-center">{backendError}</div>
                <Form onSubmit={handleSubmit}>
                  <div className="patient-history patient-info">
                    <div className="row">
                      <div className="col-xl-8 col-lg-9 col-md-10 mx-auto">
                        {/* Patient Search */}
                        <div className="patient-info-row mb-3">
                          <Row className="align-items-center">
                            <Col sm={4}>
                              <Form.Label className="appointment-label">
                                Search Patient
                              </Form.Label>
                            </Col>
                            <Col sm={8}>
                              <AsyncTypeahead
                                id="ot-patient-search"
                                isLoading={patientSearchLoading}
                                onSearch={handlePatientSearch}
                                onChange={handlePatientSelect}
                                options={patientOptions}
                                labelKey="name"
                                filterBy={() => true}
                                minLength={2}
                                useCache={false}
                                placeholder="Search by patient ID, name or phone..."
                                renderMenuItemChildren={(opt) => {
                                  const pid = getPatientIdForDoctor(
                                    opt.patientIdForDoctor,
                                    userInfo.userType === 'assistant'
                                      ? activeDoctor
                                      : userInfo,
                                  );
                                  return (
                                    <span>
                                      {pid && (
                                        <strong
                                          style={{
                                            color: '#1a85a0',
                                            marginRight: 6,
                                          }}
                                        >
                                          {pid}
                                        </strong>
                                      )}
                                      {opt.name}
                                      {opt.phoneNumber && (
                                        <span
                                          style={{
                                            color: '#888',
                                            marginLeft: 6,
                                          }}
                                        >
                                          ({opt.phoneNumber})
                                        </span>
                                      )}
                                    </span>
                                  );
                                }}
                              />
                            </Col>
                          </Row>
                        </div>

                        <Row>
                          {/* Patient Name */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Patient Name
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.patient_name}
                                onChange={(e) =>
                                  handleChange('patient_name', e.target.value)
                                }
                                placeholder="Patient name"
                              />
                            </Form.Group>
                          </Col>
                          {/* age */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Age
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.age}
                                onChange={(e) =>
                                  handleChange('age', e.target.value)
                                }
                                placeholder="age"
                              />
                            </Form.Group>
                          </Col>
                          {/* Patient ID */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Patient ID
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.patientDocId}
                                readOnly
                                placeholder="Auto-filled on patient selection"
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </Form.Group>
                          </Col>
                          {/* Registration No */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Registration No
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.registration_no}
                                onChange={(e) =>
                                  handleChange(
                                    'registration_no',
                                    e.target.value,
                                  )
                                }
                                placeholder="Registration number"
                              />
                            </Form.Group>
                          </Col>
                          {/* Ward / Bed */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Ward / Bed
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.ward_bed}
                                onChange={(e) =>
                                  handleChange('ward_bed', e.target.value)
                                }
                                placeholder="Ward / Bed"
                              />
                            </Form.Group>
                          </Col>
                          {/* Indication */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Indication
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.indication}
                                onChange={(e) =>
                                  handleChange('indication', e.target.value)
                                }
                                placeholder="Indication"
                              />
                            </Form.Group>
                          </Col>
                          {/* Operation */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Operation
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={formData.operation}
                                onChange={(e) =>
                                  handleChange('operation', e.target.value)
                                }
                                placeholder="Operation"
                              />
                            </Form.Group>
                          </Col>
                          {/* Anaesthesia */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Anaesthesia
                              </Form.Label>
                              <NoteDropdown
                                value={formData.anaesthesia}
                                onChange={(val) =>
                                  handleChange('anaesthesia', val)
                                }
                                placeholder="Add Anaesthesia"
                                suggestions={anaesthesiaSuggestions}
                              />
                            </Form.Group>
                          </Col>
                          {/* Surgeons Name */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Surgeons Name
                              </Form.Label>
                              <NoteDropdown
                                value={formData.surgeons_name}
                                onChange={(val) =>
                                  handleChange('surgeons_name', val)
                                }
                                placeholder="Add Surgeons Name"
                                suggestions={surgeonSuggestions}
                              />
                            </Form.Group>
                          </Col>
                          {/* Eye */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label d-block">
                                Eye
                              </Form.Label>
                              <ButtonGroup size="sm">
                                {['Left', 'Right', 'Both'].map((opt) => (
                                  <Button
                                    key={opt}
                                    variant={
                                      formData.eye === opt
                                        ? 'primary'
                                        : 'outline-secondary'
                                    }
                                    onClick={() => handleChange('eye', opt)}
                                    type="button"
                                  >
                                    {opt}
                                  </Button>
                                ))}
                              </ButtonGroup>
                            </Form.Group>
                          </Col>
                          {/* Status */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label d-block">
                                Status
                              </Form.Label>
                              <ButtonGroup size="sm">
                                {[
                                  {
                                    value: 'pending',
                                    label: 'Pending',
                                    active: 'warning',
                                    outline: 'outline-warning',
                                  },
                                  {
                                    value: 'scheduled',
                                    label: 'Scheduled',
                                    active: 'primary',
                                    outline: 'outline-primary',
                                  },
                                  {
                                    value: 'completed',
                                    label: 'Completed',
                                    active: 'success',
                                    outline: 'outline-success',
                                  },
                                  {
                                    value: 'cancelled',
                                    label: 'Cancelled',
                                    active: 'danger',
                                    outline: 'outline-danger',
                                  },
                                ].map(({ value, label, active, outline }) => (
                                  <Button
                                    key={value}
                                    variant={
                                      formData.status === value
                                        ? active
                                        : outline
                                    }
                                    onClick={() =>
                                      handleChange('status', value)
                                    }
                                    type="button"
                                  >
                                    {label}
                                  </Button>
                                ))}
                              </ButtonGroup>
                            </Form.Group>
                          </Col>
                          {/* Date */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="appointment-label">
                                Operation Date
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                type="date"
                                value={formData.date}
                                onChange={(e) =>
                                  handleChange('ot_date', e.target.value)
                                }
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="text-end mt-2">
                          <Button
                            variant="outline-secondary"
                            className="me-2 mr-1"
                            type="button"
                            onClick={() => navigate('/ot-list')}
                          >
                            Cancel
                          </Button>
                          {loadingStatus ? (
                            <LoadingButton />
                          ) : (
                            <Button
                              type="submit"
                              variant="primary"
                              className="btn-color"
                            >
                              Save OT
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </Row>
      </div>
    </div>
  );
}

export default NewOtEntry;
